import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AddIcon from '@mui/icons-material/Add';
import {
  KlaraBrief,
  KlaraInputs,
  KlaraProject,
  KlaraVariables,
} from '../../model/klara';
import { useAppTranslation } from '../../services/i18n';
import { FileTypeBadge } from './components/FileTypeBadge';
import { formatSize } from './format';
import { StringListField, StructuredList } from './components/StructuredList';
import { DerivedField } from './components/DerivedField';
import { LabeledField } from './components/LabeledField';
import {
  CARD_BORDER,
  DROPZONE_BACK,
  DROPZONE_BORDER,
  DROPZONE_HINT,
  DROPZONE_TEXT,
  FILE_META,
  MUTED_TEXT,
  PANEL_BACK,
} from './colors';

type Props = {
  project: KlaraProject;
  /**
   * Owned by the page, not by this tab. The topbar renders the action that
   * decides whether the form is complete, and it cannot read state that lives
   * in here — the first version of this looked at the saved project instead and
   * so never enabled until the user pressed save.
   */
  inputs: KlaraInputs;
  onInputsChange: (inputs: KlaraInputs) => void;
  onSaveInputs: () => Promise<void>;
  onAttachFile: (file: File) => Promise<void>;
  onDetachSource: (sourceId: number) => Promise<void>;
  onRequestBrief: () => Promise<void>;
  /** Lifted for the same reason as the inputs. */
  onBriefChange: (brief: KlaraBrief) => void;
  touchedFields: string[];
  onTouchField: (path: string) => void;
};

const LANGUAGES = ['cs', 'en'];

/** The same list the file picker's `accept` carries. */
const ACCEPTED = /\.(pdf|docx?|pptx?|png|jpe?g)$/i;

/**
 * Vstupy — the required fields, the source files, and the course shape Klara
 * drafts from them.
 *
 * Two halves with different lifetimes. The basics and the sources are fixed at
 * message 1: files[] ships with it and there is no documented way to add
 * sources to a live case, so leaving them editable would be a control that
 * silently does nothing to the running job. The drafted sections on the right
 * are the opposite — they exist to be edited, and they travel as the finals of
 * message 2.
 */
export function InputsTab({
  project,
  inputs,
  onInputsChange,
  onSaveInputs,
  onAttachFile,
  onDetachSource,
  onRequestBrief,
  onBriefChange,
  touchedFields,
  onTouchField,
}: Props) {
  const t = useAppTranslation();
  const [busy, setBusy] = useState(false);

  const basicsLocked = project.status !== 'draft';
  const complete =
    !!inputs.course_name?.trim() &&
    !!inputs.target_audience?.trim() &&
    !!inputs.course_length_words &&
    !!inputs.language;

  const set = <K extends keyof KlaraInputs>(key: K, value: KlaraInputs[K]) =>
    onInputsChange({ ...inputs, [key]: value });

  // Saved first: the dispatch reads the job from the database, so unsaved form
  // state would never reach the partner.
  const start = useCallback(async () => {
    setBusy(true);
    try {
      await onSaveInputs();
      await onRequestBrief();
    } finally {
      setBusy(false);
    }
  }, [onSaveInputs, onRequestBrief]);

  return (
    <>
      {/* The design opens the tab with this and puts it nowhere else — it
          explains the two halves below it, which the other tabs do not have. */}
      <Box sx={{ padding: '16px 24px 0' }}>
        <Box
          sx={{
            border: `1px solid ${CARD_BORDER}`,
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '13px',
            color: 'var(--color-black-text)',
          }}
        >
          <Box component="span" sx={{ fontWeight: 600, color: 'var(--color-black)' }}>
            {t('klara.detail.howItWorksLead')}{' '}
          </Box>
          {t('klara.detail.howItWorks')}
        </Box>
      </Box>

      {/* Stretched, not top-aligned: the ground on the right half has to run
          the height of the panel, which is what separates it from the left. */}
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
      <Box sx={{ flex: '1 1 0', minWidth: 0, padding: '24px' }}>
        <Typography sx={{ fontWeight: 600 }}>
          {t('klara.inputs.basics')}
        </Typography>
        <Typography
          sx={{
            fontSize: '13px',
            color: 'var(--color-black-text)',
            marginBottom: '20px',
          }}
        >
          {t('klara.inputs.basicsHint')}
        </Typography>

        <LabeledField label={t('klara.inputs.courseName')} required>
          <TextField
            variant="standard"
            fullWidth
            disabled={basicsLocked}
            value={inputs.course_name ?? ''}
            onChange={(e) => set('course_name', e.target.value)}
          />
        </LabeledField>
        <LabeledField label={t('klara.inputs.targetAudience')} required>
          <TextField
            variant="standard"
            fullWidth
            multiline
            disabled={basicsLocked}
            value={inputs.target_audience ?? ''}
            onChange={(e) => set('target_audience', e.target.value)}
          />
        </LabeledField>
        <Box sx={{ display: 'flex', gap: '16px' }}>
          {/* Both fields share the row evenly: the length label is long enough
              to run under the select when the field sizes itself to its value. */}
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <LabeledField label={t('klara.inputs.lengthWords')} required>
              <TextField
                variant="standard"
                fullWidth
                type="number"
                disabled={basicsLocked}
                value={inputs.course_length_words ?? ''}
                onChange={(e) =>
                  set('course_length_words', Number(e.target.value) || undefined)
                }
              />
            </LabeledField>
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            <LabeledField label={t('klara.inputs.language')} required>
              <TextField
                variant="standard"
                fullWidth
                select
                disabled={basicsLocked}
                value={inputs.language ?? 'cs'}
                onChange={(e) => set('language', e.target.value)}
              >
                {/* A project saved with a language no longer offered keeps it
                    listed, or the select would render blank. */}
                {[
                  ...LANGUAGES,
                  ...(inputs.language && !LANGUAGES.includes(inputs.language)
                    ? [inputs.language]
                    : []),
                ].map((code) => (
                  <MenuItem key={code} value={code}>
                    {t(`klara.inputs.languages.${code}`)}
                  </MenuItem>
                ))}
              </TextField>
            </LabeledField>
          </Box>
        </Box>

        <SourceFiles
          project={project}
          readOnly={basicsLocked}
          onAttachFile={onAttachFile}
          onDetachSource={onDetachSource}
        />
      </Box>

      <Box
        sx={{
          flex: '1 1 0',
          minWidth: 0,
          padding: '24px',
          backgroundColor: PANEL_BACK,
          borderLeft: `1px solid ${CARD_BORDER}`,
        }}
      >
        <DraftedBrief
          project={project}
          busy={busy}
          complete={complete}
          inputs={inputs}
          onSetInput={set}
          onStart={start}
          onBriefChange={onBriefChange}
          touchedFields={touchedFields}
          onTouchField={onTouchField}
        />
      </Box>
      </Box>
    </>
  );
}

/**
 * The course shape Klara drafts, and the action that asks her to.
 *
 * Always rendered, empty before the fill: the design shows the four sections
 * from the start so the user can see what they are about to get, and the panel
 * appearing out of nowhere after a callback would be a worse surprise than an
 * empty one.
 *
 * Editable only while the draft is the newest thing that happened. Once
 * message 2 is away the evaluation is measured against what we sent, and
 * further refinement belongs on Zadání kurzu next to the scores that judge it.
 */
function DraftedBrief({
  project,
  busy,
  complete,
  inputs,
  onSetInput,
  onStart,
  onBriefChange,
  touchedFields,
  onTouchField,
}: {
  project: KlaraProject;
  busy: boolean;
  complete: boolean;
  inputs: KlaraInputs;
  onSetInput: (key: keyof KlaraInputs, value: string) => void;
  onStart: () => Promise<void>;
  onBriefChange: (brief: KlaraBrief) => void;
  touchedFields: string[];
  onTouchField: (path: string) => void;
}) {
  const t = useAppTranslation();
  const [brief, setBrief] = useState<KlaraBrief>(project.brief ?? {});

  // The draft arrives by callback while this screen is open, so the poll has to
  // be able to fill an editor the user has not touched yet.
  useEffect(() => {
    setBrief(project.brief ?? {});
  }, [project.brief]);

  const variables = brief.variables ?? {};
  const requiredContent = brief.constants?.required_content ?? [];
  const isDraft = project.status === 'draft';
  const waiting = project.status === 'brief_requested';
  const editable = project.status === 'brief_ready';

  /**
   * Whether a field still holds what Klara sent: filled by her and not touched
   * since. Any edit ends it, even one reverted later, so the marker never puts
   * her name on someone's words.
   */
  const drafted = (key: keyof KlaraVariables) => {
    const saved = project.brief?.variables?.[key];
    const filled = Array.isArray(saved) ? saved.length > 0 : !!saved;
    return filled && !touchedFields.includes(`variables.${key}`);
  };

  const update = (next: KlaraBrief) => {
    setBrief(next);
    onBriefChange(next);
  };

  const setVariables = (patch: Partial<KlaraVariables>) => {
    Object.keys(patch).forEach((key) => onTouchField(`variables.${key}`));
    update({
      ...brief,
      variables: { ...(brief.variables ?? {}), ...patch },
    });
  };

  return (
    <Box>
      {/* Centred above the explanation, as drawn — the action is what the
          right half is for, not a control tucked beside a caption. */}
      <Box sx={{ textAlign: 'center', marginBottom: '16px' }}>
        {isDraft && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!complete || busy}
            onClick={onStart}
            sx={{ marginBottom: '12px' }}
          >
            {t('klara.inputs.letKlaraFill')}
          </Button>
        )}
        <Typography sx={{ fontSize: '13px', color: 'var(--color-black-text)' }}>
          {t('klara.inputs.klaraWillFill')}
        </Typography>
      </Box>

      {waiting && (
        <Alert severity="info" sx={{ marginBottom: '16px' }}>
          {t('klara.inputs.klaraWorking')}
        </Alert>
      )}

      {isDraft ? (
        <SeedFields inputs={inputs} onSetInput={onSetInput} busy={busy} />
      ) : (
        <>
          {/* Outline entries carry `number` and a nested `subsections` the
              design never shows; editing the title must not disturb either. */}
          <DerivedField
            label={t('klara.inputs.outline')}
            caption={t('klara.inputs.outlineHint')}
            // The frame puts this one caption in a bordered box with an icon,
            // and leaves the others as plain lines.
            captionAsNote
            drafted={drafted('outline')}
          >
            <StructuredList
              items={(variables.outline as Record<string, unknown>[]) ?? []}
              fields={[{ key: 'number', width: '72px' }, { key: 'title' }]}
              newItem={() => ({ number: '', title: '', subsections: [] })}
              onChange={(items) => setVariables({ outline: items })}
              readOnly={!editable}
              emptyHint={t('klara.inputs.awaitingKlara')}
            />
          </DerivedField>

          {/* Read-only on purpose. required_content is edited on Zadání kurzu
              as Povinný obsah, next to the evaluation that judges it —
              editable in both places, the later save would silently win. Which
              also means it is always hers, so it is tinted whenever it is
              filled. */}
          <DerivedField
            label={t('klara.inputs.keyTopics')}
            caption={t('klara.inputs.keyTopicsHint')}
            drafted={requiredContent.length > 0}
          >
            <StructuredList
              items={requiredContent as Record<string, unknown>[]}
              fields={[{ key: 'topic' }]}
              newItem={() => ({ topic: '' })}
              onChange={() => undefined}
              readOnly
              emptyHint={t('klara.inputs.awaitingKlara')}
            />
          </DerivedField>

          <DerivedField
            label={t('klara.inputs.learningObjectives')}
            caption={t('klara.inputs.learningObjectivesHint')}
            drafted={drafted('learning_objectives')}
          >
            <StringListField
              value={variables.learning_objectives}
              onChange={(value) => setVariables({ learning_objectives: value })}
              readOnly={!editable}
              emptyHint={t('klara.inputs.awaitingKlara')}
            />
          </DerivedField>
          <DerivedField
            label={t('klara.inputs.learningOutcomes')}
            caption={t('klara.inputs.learningOutcomesHint')}
            drafted={drafted('learning_outcomes')}
          >
            <StringListField
              value={variables.learning_outcomes}
              onChange={(value) => setVariables({ learning_outcomes: value })}
              readOnly={!editable}
              emptyHint={t('klara.inputs.awaitingKlara')}
            />
          </DerivedField>
        </>
      )}

      {isDraft && !complete && (
        <Typography
          variant="caption"
          sx={{ display: 'block', marginTop: '8px', color: MUTED_TEXT }}
        >
          {t('klara.inputs.requiredMissing')}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The same four sections before Klara has drafted anything: plain free text,
 * because that is what the partner's optional seeds are. They become
 * structured editors once the draft arrives, which is a different shape
 * entirely — hence two components rather than one that tries to be both.
 */
function SeedFields({
  inputs,
  onSetInput,
  busy,
}: {
  inputs: KlaraInputs;
  onSetInput: (key: keyof KlaraInputs, value: string) => void;
  busy: boolean;
}) {
  const t = useAppTranslation();

  const fields: Array<{
    key: 'outline' | 'key_topics' | 'learning_objectives' | 'learning_outcomes';
    label: string;
    hint: string;
    placeholder?: string;
  }> = [
    {
      key: 'outline',
      label: t('klara.inputs.outline'),
      hint: t('klara.inputs.outlineSeedHint'),
    },
    {
      key: 'key_topics',
      label: t('klara.inputs.keyTopics'),
      hint: t('klara.inputs.keyTopicsSeedHint'),
    },
    {
      key: 'learning_objectives',
      label: t('klara.inputs.learningObjectives'),
      hint: t('klara.inputs.learningObjectivesHint'),
      placeholder: t('klara.inputs.learningObjectivesPlaceholder'),
    },
    {
      key: 'learning_outcomes',
      label: t('klara.inputs.learningOutcomes'),
      hint: t('klara.inputs.learningOutcomesHint'),
      placeholder: t('klara.inputs.learningOutcomesPlaceholder'),
    },
  ];

  return (
    <>
      {/* The same boxed fields the drafted state uses, untinted: on this half
          of the screen every field is a box, and only the ground says whose
          words are in it. */}
      {fields.map((field) => (
        <DerivedField
          key={field.key}
          label={field.label}
          caption={field.hint}
          drafted={false}
        >
          <TextField
            variant="standard"
            fullWidth
            multiline
            minRows={4}
            disabled={busy}
            placeholder={field.placeholder}
            value={(inputs[field.key] as string) ?? ''}
            onChange={(e) => onSetInput(field.key, e.target.value)}
          />
        </DerivedField>
      ))}
    </>
  );
}

function SourceFiles({
  project,
  readOnly,
  onAttachFile,
  onDetachSource,
}: {
  project: KlaraProject;
  readOnly: boolean;
  onAttachFile: (file: File) => Promise<void>;
  onDetachSource: (sourceId: number) => Promise<void>;
}) {
  const t = useAppTranslation();
  const sources = project.sources ?? [];

  return (
    <Box
      sx={{
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '14px 18px',
          borderBottom: `1px solid ${CARD_BORDER}`,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>
          {t('klara.inputs.sources')}
        </Typography>
        <Box
          sx={{
            backgroundColor: 'var(--color-success-back)',
            color: 'var(--color-primary60)',
            borderRadius: '4px',
            padding: '2px 8px',
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            lineHeight: 1.4,
          }}
        >
          {t('klara.inputs.optional')}
        </Box>
      </Box>

      <Box sx={{ padding: '18px' }}>
      {!readOnly && (
        <DropZone onAttachFile={onAttachFile} />
      )}

      {sources.length === 0 && (
        <Typography
          sx={{
            fontSize: '12.5px',
            color: 'var(--color-black-text)',
            backgroundColor: 'var(--color-background)',
            borderRadius: '4px',
            padding: '10px 12px',
            marginTop: '12px',
          }}
        >
          {t('klara.inputs.noSources')}
        </Typography>
      )}

      {sources.length > 0 && (
        <Typography sx={{ fontWeight: 600, fontSize: '13px', margin: '16px 0 8px' }}>
          {t('klara.inputs.uploaded', { count: sources.length })}
        </Typography>
      )}

      {sources.map((source) => {
        // The design tints the whole card when a source did not make it in.
        const rejected =
          !source.readable || source.upload_status === 'failed';

        return (
        <Box
          key={source.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            height: '64px',
            padding: '0 13px',
            marginBottom: '6px',
            borderRadius: '8px',
            // Palette tokens rather than the mock's literals: these are
            // surfaces, so they should move with the theme. --color-error-back
            // is a shade off the mock's #fce1db and reads the same.
            backgroundColor: rejected
              ? 'var(--color-error-back)'
              : 'var(--color-background)',
          }}
        >
          <FileTypeBadge fileName={source.file_name} />

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* The row is a fixed 64px, so a long name has to be cut rather
                than wrapped onto a third line and out of the card. */}
            <Typography
              title={source.file_name}
              sx={{
                fontSize: '12.5px',
                // The token, not the mock's slate-900: the card underneath it
                // follows the theme, so the text on it has to as well.
                color: 'var(--color-black)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {source.file_name}
            </Typography>
            {/* The design pairs the size with a page count we do not have. */}
            {formatSize(source.size) && (
              <Typography sx={{ fontSize: '11px', color: FILE_META }}>
                {formatSize(source.size)}
              </Typography>
            )}
          </Box>

          {source.rating && (
            <RatingDot rating={source.rating} score={source.file_score} />
          )}
          {/* The design names the status beside the dot. "OK" is the only
              word we can put there: the amber and red ratings are the
              partner's raw enum, and naming a severity they never define
              would be us inventing one. */}
          {source.rating === 'green' && source.readable && (
            <Typography sx={{ fontSize: '11.5px', color: MUTED_TEXT }}>
              {t('klara.inputs.ratingOk')}
            </Typography>
          )}
          {!source.readable && (
            <Chip size="small" color="error" label={t('klara.inputs.unreadable')} />
          )}
          {source.upload_status === 'failed' && (
            <Chip size="small" color="error" label={t('klara.inputs.uploadFailed')} />
          )}

          {!readOnly && (
            <IconButton
              aria-label={t('klara.inputs.removeFile')}
              onClick={() => onDetachSource(source.id)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        );
      })}
      </Box>
    </Box>
  );
}

/**
 * Where files come in: a drop target that is also a file picker, as the design
 * draws it — the button we had could only be clicked.
 */
function DropZone({ onAttachFile }: { onAttachFile: (file: File) => Promise<void> }) {
  const t = useAppTranslation();
  const [over, setOver] = useState(false);
  const [rejected, setRejected] = useState(false);

  // The picker enforces `accept`; a drop does not, so the same list has to be
  // applied by hand — otherwise a dropped .zip is sent, fails somewhere in the
  // upload, and simply never appears in the list.
  const take = (files: FileList | null) => {
    const all = Array.from(files ?? []);
    const allowed = all.filter((file) => ACCEPTED.test(file.name));
    setRejected(allowed.length < all.length);
    allowed.forEach((file) => onAttachFile(file));
  };

  return (
    <Box
      component="label"
      onDragOver={(e: React.DragEvent) => {
        // Without both of these the browser keeps its own handling and opens
        // the file in the tab instead of giving it to us.
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e: React.DragEvent) => {
        e.preventDefault();
        setOver(false);
        take(e.dataTransfer.files);
      }}
      sx={{
        display: 'block',
        cursor: 'pointer',
        textAlign: 'center',
        padding: '26px 18px',
        borderRadius: '10px',
        border: `2px dashed ${over ? 'var(--color-primary)' : DROPZONE_BORDER}`,
        backgroundColor: over ? 'var(--color-primary15)' : DROPZONE_BACK,
      }}
    >
      <input
        hidden
        multiple
        type="file"
        accept=".pdf,.docx,.doc,.pptx,.ppt,.png,.jpg,.jpeg"
        onChange={(e) => {
          take(e.target.files);
          e.target.value = '';
        }}
      />
      <FileDownloadOutlinedIcon sx={{ fontSize: '24px', color: DROPZONE_TEXT }} />
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          color: DROPZONE_TEXT,
          marginTop: '6px',
        }}
      >
        {t('klara.inputs.dropzone')}
      </Typography>
      <Typography sx={{ fontSize: '11.5px', color: DROPZONE_HINT, marginTop: '4px' }}>
        {t('klara.inputs.fileRules')}
      </Typography>
      {rejected && (
        <Typography
          sx={{ fontSize: '11.5px', color: 'var(--color-error)', marginTop: '4px' }}
        >
          {t('klara.inputs.unsupportedFormat')}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The partner's per-file verdict, as the traffic light it is.
 *
 * `rating` is their raw enum — green / yellow / red — and neither their spec
 * nor ours says at what `file_score` the bands break. Rendering the word would
 * either leak English into the screen or make us name a severity we cannot
 * define, so the colour carries it and the score goes in the tooltip, which is
 * the one number they do give us. Matches the design, where a source row shows
 * a dot rather than a chip.
 */
function RatingDot({ rating, score }: { rating: string; score: number | null }) {
  const t = useAppTranslation();
  const label =
    score === null || score === undefined
      ? t('klara.inputs.ratingTooltip')
      : t('klara.inputs.ratingTooltipScore', { score: formatScore(score) });

  return (
    <Tooltip title={label}>
      <Box
        // A bare div takes no accessible name, so the rating would reach a
        // screen reader as colour only — which is to say, not at all.
        role="img"
        aria-label={label}
        sx={{
          width: '10px',
          height: '10px',
          borderRadius: '5px',
          flexShrink: 0,
          backgroundColor: ratingColor(rating),
        }}
      />
    </Tooltip>
  );
}

function formatScore(score: number) {
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

function ratingColor(rating: string) {
  if (rating === 'green') return 'var(--color-success)';
  if (rating === 'yellow') return 'var(--color-warning)';
  return 'var(--color-error)';
}
